import React, { memo } from 'react';
import { Sparkles, Leaf, Award, Star } from 'lucide-react';

import { usePreviewPanel } from '../../contexts/PreviewPanelContext';

const CONTROL_PLANE_URL = 'https://agentic-commerce-control-plane.pages.dev';

const QUALITY_BADGES = {
  luxury: { label: 'Luxury', className: 'bg-amber-100 text-amber-800', icon: Award },
  premium: { label: 'Premium', className: 'bg-purple-100 text-purple-800', icon: Star },
  'mid-range': { label: 'Quality', className: 'bg-blue-100 text-blue-800', icon: null },
  budget: { label: 'Value', className: 'bg-green-100 text-green-800', icon: Leaf },
};

const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return '/images/placeholder.svg';
  if (imageUrl.startsWith('/assets/')) {
    return `${CONTROL_PLANE_URL}${imageUrl}`;
  }
  return imageUrl;
};

const formatPrice = (price) => {
  if (!price) return '-';
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  return `£${numPrice.toFixed(2)}`;
};

/**
 * ComparisonView - Side-by-side product comparison
 */
const ComparisonView = memo(function ComparisonView() {
  const { products, openProduct } = usePreviewPanel();

  if (!products || products.length < 2) {
    return (
      <div className="p-6 text-center text-penta-grey-medium">
        Select at least 2 products to compare
      </div>
    );
  }

  // Comparison attributes
  const attributes = [
    { key: 'price', label: 'Price', format: formatPrice },
    { key: 'brand', label: 'Brand' },
    { key: 'qualityTier', label: 'Quality' },
    { key: 'condition', label: 'Condition' },
    { key: 'sustainability', label: 'Eco-Friendly' },
  ];

  return (
    <div className="p-4">
      {/* Product headers - scrollable horizontally */}
      <div className="flex gap-3 overflow-x-auto pb-4 mb-4 border-b border-neutral-100">
        {products.map((product) => (
          <button
            key={product.id || product.productId}
            onClick={() => openProduct(product.id || product.productId, product)}
            className="flex-shrink-0 w-32 text-center"
          >
            <div className="aspect-square relative bg-neutral-50 rounded-lg overflow-hidden mb-2">
              <img
                src={resolveImageUrl(product.image)}
                alt={product.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <p className="text-xs font-medium text-penta-black line-clamp-2">
              {product.title}
            </p>
          </button>
        ))}
      </div>

      {/* Comparison table */}
      <div className="space-y-3">
        {attributes.map(({ key, label, format }) => (
          <div key={key} className="flex items-start gap-3">
            <div className="w-24 flex-shrink-0 text-sm text-penta-grey-medium pt-1">
              {label}
            </div>
            <div className="flex-1 flex gap-3 overflow-x-auto">
              {products.map((product) => {
                const value = product[key];
                const displayValue = format ? format(value) : value || '-';

                return (
                  <div
                    key={product.id || product.productId}
                    className="flex-shrink-0 w-32 text-sm text-penta-black"
                  >
                    {key === 'qualityTier' && value ? (
                      <QualityBadge tier={value} />
                    ) : key === 'sustainability' && value ? (
                      <div className="flex items-center gap-1 text-green-700">
                        <Leaf size={14} />
                        <span className="text-xs">Yes</span>
                      </div>
                    ) : (
                      <span className={value ? '' : 'text-neutral-300'}>
                        {displayValue}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* AI explanations */}
        <div className="flex items-start gap-3 pt-3 border-t border-neutral-100">
          <div className="w-24 flex-shrink-0 text-sm text-penta-grey-medium pt-1 flex items-center gap-1">
            <Sparkles size={14} className="text-penta-mint" />
            Why it&apos;s a fit
          </div>
          <div className="flex-1 flex gap-3 overflow-x-auto">
            {products.map((product) => (
              <div
                key={product.id || product.productId}
                className="flex-shrink-0 w-32 text-xs text-penta-grey-dark"
              >
                {product.explanation || '-'}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

const QualityBadge = memo(function QualityBadge({ tier }) {
  const badge = QUALITY_BADGES[tier?.toLowerCase()];
  if (!badge) return <span className="capitalize">{tier || '-'}</span>;

  const BadgeIcon = badge.icon;
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${badge.className}`}>
      {BadgeIcon && <BadgeIcon size={12} />}
      {badge.label}
    </div>
  );
});

export default ComparisonView;
